import { useCallback, useContext, useRef } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useCalculateTaxByZipCode, useHandleDefaultTax } from '@/api/tax';
import { getCustomerCountry } from '@/utils/customer';
import { isCountryCodeIsUSAOrCanada, shouldUseWalletDerivedZipTax } from '@/utils/tax';
import { canonicalizeZipCode, formatZipCodeByCountry, getCurrentCheckoutZipCode } from '@/utils/zipCode';
import { logError, logInfo, logWarn } from '@/utils/logger';
import { ErrorLogs, InfoLogs } from '@/constants/logs';
import {
  WalletZipTaxSource,
  WalletZipTaxStatus,
  type WalletZipTaxLogFields,
  type WalletZipTaxOutcome,
  type WalletZipTaxResult,
} from '@/types/walletZipTax';

/**
 * ACDEV-25067: Shared wallet-ZIP tax lookup used by every wallet integration
 * (Stripe Apple/Google Pay via ECE, Adyen Apple Pay, Adyen Google Pay).
 *
 * Encapsulates the parts that MUST behave identically across wallets:
 *   - Session gate: `shouldUseWalletDerivedZipTax(customer, featureFlags, sessionStartedWithoutValidTax)`
 *     (US/CA session AND (`WALLET_ZIP_TAX` ON OR the session started without
 *     a resolved US/CA tax — ACDEV-27914)).
 *   - CA postal-code normalization off the WALLET's country (never the
 *     session country — a US-session buyer with a CA wallet address, or vice
 *     versa, must still be normalized on the wallet's actual jurisdiction).
 *   - Endpoint routing: `/calculate-estimated-tax` ONLY when the wallet
 *     country matches the (US/CA) session country — a cross-jurisdiction
 *     wallet (US session + CA wallet, or vice versa) can't produce a valid
 *     tax lookup against the session's tax config, so we route those to
 *     `/handle-default-tax` alongside every non-US/CA wallet country. Any
 *     `isValid: false` response falls back to `/handle-default-tax` so the
 *     sheet always gets a breakdown; the fallback result is tagged
 *     `DEFAULT` (non-authoritative). Both endpoints return
 *     `CalculateEstimatedTaxResponse`.
 *   - `capturedWalletTaxRef` lifecycle: written on success, CLEARED on invalid/error
 *     (so the finalize step never forwards a stale ZIP after the buyer changes
 *     to an unsupported jurisdiction) and CLEARED after `applyWalletTaxToCheckout()`.
 *
 * Callers own the wallet-specific translation of the outcome (build sheet
 * update payloads, resolve/reject the sheet callback, log with their own
 * label) — this hook is deliberately side-effect free apart from the ref.
 *
 * `applyWalletTaxToCheckout()` is the SAFE-window escape hatch: at wallet cancel
 * / finalize failure the sheet is already tearing down, so writing the
 * wallet-derived tax + ZIP + state to `CheckoutContext` no longer risks
 * unmounting a live wallet element mid-sheet. It's a no-op if nothing valid
 * has been captured (ref is null), and it self-clears the ref after write so
 * a subsequent cancel of a fresh sheet with no new tax can't re-flush stale
 * data. When the stored result came from `/handle-default-tax` AND the
 * checkout already holds a valid tax breakdown, it skips the flush (the
 * default is not authoritative for the buyer's jurisdiction, so it must not
 * overwrite a real tax the buyer had before opening the wallet) AND
 * fire-and-forget re-issues `/calculate-estimated-tax` for the previous
 * (valid) ZIP so the BE session — which the wallet flow's default-tax call
 * dirtied — is restored to the same tax the UI is still showing. `CALCULATE`
 * results (real US/CA-matched jurisdiction lookup) always flush.
 */
export const useWalletZipTaxCalculator = () => {
  const {
    customer,
    pricing,
    addressDetails,
    featureFlags,
    sessionStartedWithoutValidTax,
    actions: { updateAddressDetails, updatePriceBreakdown },
  } = useContext(CheckoutContext);
  const { mutateAsync: calculateTaxByZipCode } = useCalculateTaxByZipCode();
  const { mutateAsync: handleDefaultTax } = useHandleDefaultTax();
  const capturedWalletTaxRef = useRef<WalletZipTaxResult | null>(null);
  // Mirror the current checkout total in a ref so `runWalletZipTax`'s
  // identity is NOT recomputed on every pricing change. Keeping `pricing`
  // in the `useCallback` deps would invalidate the callback whenever the
  // parent updates the price breakdown — which in turn would re-invoke
  // memoized wallet handlers and re-mount live wallet sheets mid-flow.
  // Refs are read at call time, so the comparison still sees the latest
  // context total without coupling it to the callback's identity.
  const currentCheckoutPriceTotalRef = useRef(pricing.priceBreakdown.priceTotal);
  currentCheckoutPriceTotalRef.current = pricing.priceBreakdown.priceTotal;
  // Same rationale as `currentCheckoutPriceTotalRef`: read the current `isTaxValid` at
  // call time so `applyWalletTaxToCheckout` can decide whether to preserve
  // an already-authoritative tax without pulling `pricing` into the deps
  // (which would invalidate the callback on every context write). Name
  // spells out that this mirrors the LIVE value — NOT a snapshot taken at
  // the moment the wallet opened.
  const currentCheckoutTaxIsValidRef = useRef(pricing.priceBreakdown.isTaxValid);
  currentCheckoutTaxIsValidRef.current = pricing.priceBreakdown.isTaxValid;
  // Same rationale as `currentCheckoutPriceTotalRef`: capture the current checkout ZIP in a
  // ref so the SAME_ZIP short-circuit can read the latest value without pulling
  // `addressDetails` into the `useCallback` deps (which would invalidate the
  // callback every time `updateAddressDetails` fires — including the write from
  // `applyWalletTaxToCheckout` itself).
  const currentCheckoutZipRef = useRef<string>(getCurrentCheckoutZipCode(addressDetails, customer));
  currentCheckoutZipRef.current = getCurrentCheckoutZipCode(addressDetails, customer);

  const runWalletZipTax = useCallback(
    async (
      rawZip: string | null | undefined,
      walletCountry: string | null | undefined
    ): Promise<WalletZipTaxOutcome> => {
      const trimmedZip = rawZip?.trim() ?? '';
      const country = walletCountry?.toUpperCase() ?? '';
      const sessionCountry = getCustomerCountry(customer).toUpperCase();
      // Read at log time, not at entry: the BE call below is async and the
      // checkout tax/ZIP can move before we exit.
      const currentLogFields = (): WalletZipTaxLogFields => ({
        hasWalletZip: !!trimmedZip,
        walletCountry: country,
        sessionCountry,
        checkoutTaxValid: currentCheckoutTaxIsValidRef.current,
        hasCheckoutZip: !!currentCheckoutZipRef.current,
      });

      // Belt-and-suspenders outer try/catch: NOTHING below (predicate check,
      // ZIP normalization, jurisdiction resolution, BE call, ref writes) is
      // allowed to break the caller's payment flow. Every throw funnels to
      // the structured RUM log + safe `ERROR` outcome so the sheet callback
      // can resolve with the current total instead of hanging.
      try {
        if (!shouldUseWalletDerivedZipTax(customer, featureFlags, sessionStartedWithoutValidTax)) {
          logWalletZipTaxOutcome(WalletZipTaxStatus.SKIPPED, currentLogFields(), { reason: 'gate_closed' });
          return { status: WalletZipTaxStatus.SKIPPED };
        }

        // Wallet gave us no ZIP AND the checkout already holds a valid tax:
        // the pre-open tax IS the buyer's authoritative jurisdiction, so
        // calling `/handle-default-tax` would only dirty the BE session
        // (which `applyWalletTaxToCheckout` would then have to resync on
        // cancel) and swap the sheet's correct total for a lower default —
        // no user-visible gain, real risk of undercharge if the buyer
        // authorizes against the default. Skip the BE round-trip and let
        // the sheet resolve with the current (correct) total.
        if (!trimmedZip && currentCheckoutTaxIsValidRef.current) {
          logWalletZipTaxOutcome(WalletZipTaxStatus.SKIPPED, currentLogFields(), { reason: 'no_zip_with_valid_tax' });
          return { status: WalletZipTaxStatus.SKIPPED };
        }

        const normalizedZip = trimmedZip ? formatZipCodeByCountry(trimmedZip, country) : '';
        // Two related-but-distinct flags:
        //   `isWalletJurisdictionMatch` — the wallet's country is US/CA AND
        //     matches the session country. A cross-jurisdiction wallet (US
        //     session + CA wallet, or vice versa) can't be priced by the
        //     session's tax rules regardless of ZIP presence.
        //   `canCallCalculateTax` — jurisdiction-match AND the wallet
        //     actually gave us a ZIP. `/calculate-estimated-tax` needs a
        //     postal code, so a US-session US-wallet with no ZIP (Apple Pay
        //     contact with only country, Google Pay's redacted address that
        //     stripped the ZIP) still falls through to `/handle-default-tax`.
        // Kept separate so the `walletZipTaxInvalid` RUM log can slice
        // "cross-jurisdiction" vs "jurisdiction match but no ZIP" vs "real
        // endpoint said invalid".
        const isWalletJurisdictionMatch = isCountryCodeIsUSAOrCanada(country) && country === sessionCountry;
        const canCallCalculateTax = isWalletJurisdictionMatch && !!trimmedZip;

        // Same ZIP as checkout already holds → tax we've got is correct for this
        // ZIP; skip the BE round-trip so the sheet can resolve immediately.
        // Compare case-insensitively AND whitespace-insensitively so CA formats
        // like "M5V3L9" and "M5V 3L9" match, and US "10001" matches "10001 ".
        // Requires a non-empty wallet ZIP: without one there's nothing to
        // compare against, and we'd rather hit `/handle-default-tax` so the
        // sheet gets a breakdown than short-circuit to SAME_ZIP.
        if (
          trimmedZip &&
          currentCheckoutZipRef.current &&
          canonicalizeZipCode(normalizedZip) === canonicalizeZipCode(currentCheckoutZipRef.current)
        ) {
          logWalletZipTaxOutcome(WalletZipTaxStatus.SAME_ZIP, currentLogFields());
          return { status: WalletZipTaxStatus.SAME_ZIP };
        }

        // First try `/calculate-estimated-tax` on the wallet's ZIP when the
        // buyer is eligible; otherwise start on `/handle-default-tax`.
        let taxResponse = canCallCalculateTax
          ? await calculateTaxByZipCode({ zipCode: normalizedZip })
          : await handleDefaultTax();
        let source = canCallCalculateTax ? WalletZipTaxSource.CALCULATE : WalletZipTaxSource.DEFAULT;

        // Fallback: any `isValid: false` (BE couldn't price for this ZIP —
        // unrecognized US/CA postal code, no tax config for the region,
        // etc.) falls through to `/handle-default-tax` so the sheet still
        // gets a breakdown instead of a hard INVALID. The default endpoint
        // is expected to always return a valid breakdown, so this makes
        // the INVALID outcome effectively unreachable in the happy BE path;
        // the guard below stays as a safety net if the default endpoint
        // ever regresses. Mark the fallback result as `DEFAULT` so
        // `applyWalletTaxToCheckout` treats it as non-authoritative and
        // preserves any pre-open valid tax.
        if (!taxResponse.isValid) {
          logWarn(InfoLogs.walletZipTaxInvalid, {
            walletCountry: country,
            sessionCountry,
            isWalletJurisdictionMatch,
            canCallCalculateTax,
          });
          taxResponse = await handleDefaultTax();
          source = WalletZipTaxSource.DEFAULT;
        }

        const { isValid, priceBreakdown, zipCode, state } = taxResponse;
        const endpointDetail = {
          source,
          endpoint: source === WalletZipTaxSource.CALCULATE ? 'calculate-estimated-tax' : 'handle-default-tax',
          fellBackToDefault: canCallCalculateTax && source === WalletZipTaxSource.DEFAULT,
        };

        if (!isValid) {
          capturedWalletTaxRef.current = null;
          logWalletZipTaxOutcome(WalletZipTaxStatus.INVALID, currentLogFields(), endpointDetail);
          return { status: WalletZipTaxStatus.INVALID };
        }

        const result: WalletZipTaxResult = {
          zipCode: zipCode || normalizedZip,
          state,
          priceBreakdown,
          // Track the origin so `applyWalletTaxToCheckout` can decide whether
          // it's safe to overwrite an existing valid tax at cancel/failure —
          // only `CALCULATE` results are authoritative for the buyer's actual
          // jurisdiction. A jurisdiction-matched call that fell back to
          // `/handle-default-tax` is tagged `DEFAULT` here.
          source,
        };
        capturedWalletTaxRef.current = result;
        // Total didn't move — tell callers so they can skip any sheet-total
        // update path (e.g. Stripe's `elements.update({ amount })`).
        if (priceBreakdown.priceTotal === currentCheckoutPriceTotalRef.current) {
          logWalletZipTaxOutcome(WalletZipTaxStatus.UNCHANGED, currentLogFields(), endpointDetail);
          return { status: WalletZipTaxStatus.UNCHANGED, result };
        }
        logWalletZipTaxOutcome(WalletZipTaxStatus.SUCCESS, currentLogFields(), endpointDetail);
        return { status: WalletZipTaxStatus.SUCCESS, result };
      } catch (error) {
        capturedWalletTaxRef.current = null;
        logError(ErrorLogs.walletZipTaxError, error, {
          hasWalletZipCode: !!trimmedZip,
          walletCountry: country,
        });
        return { status: WalletZipTaxStatus.ERROR, error };
      }
    },
    [customer, featureFlags, sessionStartedWithoutValidTax, calculateTaxByZipCode, handleDefaultTax]
  );

  // Wallet-flow default-tax calls persist a default breakdown to the BE
  // session — if we walk away from the wallet holding a real, previously-valid
  // tax, finalize would otherwise pay against that stale default. Callers
  // pass the ZIP they want the BE session realigned to (typically the buyer's
  // pre-open, still-valid ZIP). Fire-and-forget by design: flushing the
  // response would race any wallet the buyer opens next. Errors are logged
  // to RUM (previously swallowed silently) so a BE hiccup here is
  // observable — the BE-side session stays on the default until the next
  // real user action re-triggers `/calculate-estimated-tax`, and a spike
  // in `walletZipTaxResyncFailed` correlated with wallet-cancel volume is
  // the signal that a buyer's next finalize may charge against the stale
  // default tax.
  // (Declared above `applyWalletTaxToCheckout` only because `useCallback`
  // resolves the deps at render time — logically it's a helper of the latter.)
  const resyncBackendSessionTax = useCallback(
    (zipCode: string) => {
      if (!zipCode) return;
      calculateTaxByZipCode({ zipCode }).catch((error: unknown) => {
        logError(ErrorLogs.walletZipTaxResyncFailed, error, { zipCode });
      });
    },
    [calculateTaxByZipCode]
  );

  // Writes a wallet-derived tax result into `CheckoutContext`. `AddressDetails.address`
  // is required — preserve whatever the buyer already had. `state` gets the
  // BE-canonical value from the tax response when we have one, falling back
  // to the previous state so we never clobber an existing value with
  // `undefined`. (Declared above `applyWalletTaxToCheckout` only because
  // `useCallback` resolves the deps at render time — logically a helper of
  // the latter.)
  const commitWalletTaxResult = useCallback(
    (capturedWalletTax: WalletZipTaxResult) => {
      updateAddressDetails({
        address: addressDetails?.address ?? customer.address?.address ?? '',
        zipCode: capturedWalletTax.zipCode,
        state: capturedWalletTax.state ?? addressDetails?.state ?? customer.address?.state,
      });
      updatePriceBreakdown(capturedWalletTax.priceBreakdown);
    },
    [customer, addressDetails, updateAddressDetails, updatePriceBreakdown]
  );

  const applyWalletTaxToCheckout = useCallback(() => {
    const capturedWalletTax = capturedWalletTaxRef.current;
    if (!capturedWalletTax) return;
    capturedWalletTaxRef.current = null;
    // Default-tax results are NOT authoritative for the buyer's jurisdiction
    // (they come from a non-US/CA or cross-jurisdiction wallet). If the
    // checkout already holds a valid tax breakdown — meaning the buyer had a
    // real jurisdiction resolved before the wallet flow — preserve it rather
    // than clobber it with the default breakdown. Only flush the default when
    // there was nothing valid to protect (checkout was in an invalid-tax
    // state, so the default is a strict upgrade). `CALCULATE` results (from
    // `/calculate-estimated-tax` for a matching US/CA wallet) are always
    // safe to flush.
    if (capturedWalletTax.source === WalletZipTaxSource.DEFAULT && currentCheckoutTaxIsValidRef.current) {
      logInfo(InfoLogs.walletZipTaxSuppressed, {
        walletZipCode: capturedWalletTax.zipCode,
        preOpenZipCode: currentCheckoutZipRef.current,
      });
      resyncBackendSessionTax(currentCheckoutZipRef.current);
    } else {
      logInfo(InfoLogs.walletZipTaxApplied, {
        source: capturedWalletTax.source,
        zipCode: capturedWalletTax.zipCode,
        preOpenTaxWasValid: currentCheckoutTaxIsValidRef.current,
      });
      commitWalletTaxResult(capturedWalletTax);
    }
  }, [commitWalletTaxResult, resyncBackendSessionTax]);

  return { runWalletZipTax, capturedWalletTaxRef, applyWalletTaxToCheckout };
};

// Logs one non-throwing exit of `runWalletZipTax`. Does not decide the
// outcome — the caller returns `{ status }` itself, so the branch that ran
// is visible at the call site.
const logWalletZipTaxOutcome = (
  status: WalletZipTaxStatus,
  fields: WalletZipTaxLogFields,
  detail: Record<string, string | number | boolean> = {}
): void => {
  logInfo(InfoLogs.walletZipTaxOutcome, { status, ...fields, ...detail });
};
